import { useEffect, useRef, useState } from 'react';
import { enterOpenSession, Forbidden, getSession, loadConfig, onSessionEvent, renderGoogleButton, restore, signInWithCredential, signOut, type AuthConfig } from '../auth/session.ts';
import { App } from './App.tsx';

type GateState = 'loading' | 'signed-out' | 'forbidden' | 'ready';

export function Gate() {
  const [state, setState] = useState<GateState>('loading');
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const button = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const cfg = await loadConfig();
        if (cancel) return;
        setConfig(cfg);
        if (cfg.authRequired === false) {
          await enterOpenSession();
          if (!cancel) setState('ready');
          return;
        }
        const me = await restore();
        if (cancel) return;
        if (!me) setState('signed-out');
        else setState('ready');
      } catch (err) {
        if (cancel) return;
        if (err instanceof Forbidden) {
          setEmail(err.email);
          setState('forbidden');
          return;
        }
        setError(err instanceof Error ? err.message : 'Could not reach the editor');
        setState('signed-out');
      }
    })().catch(() => {
      if (!cancel) setState('signed-out');
    });
    const off = onSessionEvent((event) => {
      if (cancel) return;
      if (event === 'signed-out') {
        setEmail(null);
        setState('signed-out');
      } else {
        setEmail(getSession()?.email ?? null);
        setState('forbidden');
      }
    });
    return () => {
      cancel = true;
      off();
    };
  }, []);

  useEffect(() => {
    const parent = button.current;
    if (state !== 'signed-out' || !config?.googleClientId || !parent) return;
    let cancel = false;
    renderGoogleButton(parent, (credential) => {
      setError(null);
      signInWithCredential(credential).then(
        () => {
          if (!cancel) setState('ready');
        },
        (err: unknown) => {
          if (cancel) return;
          if (err instanceof Forbidden) {
            setEmail(err.email);
            setState('forbidden');
            return;
          }
          setError(err instanceof Error ? err.message : 'Sign-in failed');
        },
      );
    }).catch((err: unknown) => {
      if (!cancel) setError(err instanceof Error ? err.message : 'Could not load Google sign-in');
    });
    return () => {
      cancel = true;
    };
  }, [state, config]);

  if (state === 'loading') return <div className="loading">Checking sign-in…</div>;
  if (state === 'ready') return <App />;
  if (state === 'forbidden') {
    return (
      <div className="gate">
        <div className="gate-card">
          <h1>No access</h1>
          <p>{email ? `${email} is signed in, but that account cannot use the level editor.` : 'This account cannot use the level editor.'} A Cactus Fiesta admin can add it from the People window.</p>
          <button className="btn" onClick={() => void signOut()}>Sign out</button>
        </div>
      </div>
    );
  }
  return (
    <div className="gate">
      <div className="gate-card">
        <h1>Level editor</h1>
        <p>Sign in with the Google account you use for Cactus Fiesta. Admins can open the editor. Everyone else needs an admin to add them.</p>
        {error && <p className="warn">{error}</p>}
        {config && !config.googleClientId && <p className="warn">Sign-in is not configured on this server yet.</p>}
        <div ref={button} className="google-button" />
      </div>
    </div>
  );
}
